import { CLUSTER_SIZE_TO_EXECUTORS } from '../constants';
import { QueryService } from '../types/queryservice';

/** Reverse of CLUSTER_SIZE_TO_EXECUTORS: executor count → preset label. */
const EXECUTORS_TO_SIZE_LABEL: Record<number, string> = Object.entries(
  CLUSTER_SIZE_TO_EXECUTORS
).reduce<Record<number, string>>((acc, [size, count]) => {
  acc[count] = size.toUpperCase();
  return acc;
}, {});

/**
 * Turn an executor count into the size label shown in the list: a preset
 * (XS/S/M/L/XL/XXL) when the count matches one, otherwise "Custom (N)".
 *
 * The size concept lives only in the UI — the backend stores a plain count
 * (baseSizeExecutors); this is where it becomes a human label (PLT-8787).
 */
export function clusterSizeLabel(executors: number): string {
  return EXECUTORS_TO_SIZE_LABEL[executors] ?? `Custom (${executors})`;
}

/**
 * Pick the executor count to render in the Size column for a legacy cluster
 * that has no persisted baseSizeExecutors. Order:
 *   1. live runtime count (executorReplicas, then currentExecutors)
 *   2. autoscaling ceiling (maxExecutors), then floor (minExecutors)
 *
 * New clusters carry baseSizeExecutors and never reach this fallback.
 */
export function getRepresentativeExecutorCount(row: QueryService): number | undefined {
  if (typeof row.executorReplicas === 'number' && row.executorReplicas > 0) {
    return row.executorReplicas;
  }
  if (typeof row.currentExecutors === 'number' && row.currentExecutors > 0) {
    return row.currentExecutors;
  }
  if (typeof row.maxExecutors === 'number' && row.maxExecutors > 0) {
    return row.maxExecutors;
  }
  if (typeof row.minExecutors === 'number' && row.minExecutors > 0) {
    return row.minExecutors;
  }
  return undefined;
}

const EM_DASH = '—';

/**
 * Executor count for the "Active" side of Active/Max: executors actually
 * Ready right now (PLT-9852). A suspended cluster has nothing running, so 0 —
 * even though the API reports the pre-suspension replica count on
 * executorReplicas (the backend restores it so the size stays visible).
 * Otherwise readyExecutors; when the backend predates that field, fall back
 * to the desired count (executorReplicas, then currentExecutors) — the
 * pre-PLT-9852 behaviour.
 */
export function getActiveExecutors(row: QueryService): number | undefined {
  const suspended = typeof row.status === 'string' && row.status.toLowerCase() === 'suspended';
  if (suspended) return 0;
  if (typeof row.readyExecutors === 'number') return row.readyExecutors;
  if (typeof row.executorReplicas === 'number') return row.executorReplicas;
  if (typeof row.currentExecutors === 'number') return row.currentExecutors;
  return undefined;
}

/**
 * Executors the cluster is still bringing up: desired (executorReplicas)
 * minus Ready. Only meaningful when the backend reports readyExecutors and
 * the cluster isn't suspended; clamped at 0 for scale-downs, where ready
 * can briefly exceed the new desired count.
 */
export function getStartingExecutors(row: QueryService): number {
  const suspended = typeof row.status === 'string' && row.status.toLowerCase() === 'suspended';
  if (suspended) return 0;
  if (typeof row.readyExecutors !== 'number' || typeof row.executorReplicas !== 'number') {
    return 0;
  }
  return Math.max(0, row.executorReplicas - row.readyExecutors);
}

/**
 * Render the "Active / Max" cell (DBR-style).
 *   - Active = executors actually Ready (see getActiveExecutors) — not the
 *     desired spec count, which used to overstate capacity during ramps
 *     (PLT-9852).
 *   - Max    = configured ceiling. When autoscaling is on, that's maxExecutors.
 *     When off, the fixed executor count (minExecutors === maxExecutors === size).
 *
 * Returns "{active} / {max}" when at least Max is known, otherwise an em dash.
 */
export function formatActiveMax(row: QueryService): string {
  const active = getActiveExecutors(row);

  const max =
    typeof row.maxExecutors === 'number' && row.maxExecutors > 0
      ? row.maxExecutors
      : typeof row.minExecutors === 'number' && row.minExecutors > 0
        ? row.minExecutors
        : undefined;

  if (max === undefined) return EM_DASH;
  return `${active ?? 0} / ${max}`;
}
